import { h } from "preact";
import { RoutableProps } from "preact-router";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import base32Encode from "base32-encode";
import "./styles.scss";
import { TemplatePrint } from "../../../components/TemplatePrint";
import { resolvePath, utilsEvent as utilsEventUrl, utilsWsReq } from "../../../api";
import { Alert } from "../../../components/Alert";
import { useEventSource } from "../../../hooks";
import { egzotechAgentManager, route } from "../../../App";
import { ImageSlider } from "../../../components/ImageSlider";
import { uriRecord, encodeMessage, decodeMessage } from "ndef";
import { addProductToDatabase } from "../../../api/product-api";
import { generateLabelFields } from "../../../utils";

const stellaBIOTemplate = "Stella BIO/Serial sticker Stella BIO/Serial sticker Stella BIO brotherTZe-S221 9mm 20201022.svg";

export function StellaBIOFlash(props: RoutableProps) {
    const [mac, setMac] = useState("");
    const [macState, setMacState] = useState("");
    const [readMacRefresh, setReadMacRefresh] = useState(0);
    const [stellaData, setStellaData] = useState(null as null | { [key: string]: any });
    const [hasPrintedLabel, setHasPrintedLabel] = useState(false);
    const [stellaDataStatus, setStellaDataStatus] = useState("program");
    const [nfcReady, setNfcReady] = useState("");
    const [nfcStatus, setNfcStatus] = useState("");
    const [nfcRefresh, setNfcRefresh] = useState(0);
    const [flashConsoleOutput, setFlashConsoleOutput] = useState("");
    const [flashStart, setFlashStart] = useState(false);
    const [flashProgress, setFlashProgress] = useState(0);
    const flashConsoleOutputRef = useRef<HTMLDivElement>(null);
    const [autoProgramming, setAutoProgramming] = useState(true);

    let arr = null;

    if (mac) {
        const macSplit = mac.split(":");

        if (macSplit.length === 6) {
            macSplit.splice(1, 1);
            arr = new Uint8Array([...macSplit.map(s => parseInt(s, 16))]);
        }
    }

    const serial = arr ? base32Encode(arr.buffer, "RFC4648") : "";

    const programNFC = useCallback(() => {
        const message = [
            uriRecord(`https://app.egzotech.com/nfc/${serial}`)
        ];

        const bytes = encodeMessage(message);
        const output = bytes.map(b => b.toString(16)).join(",");

        setNfcReady("");
        setNfcStatus("program");
        utilsWsReq(egzotechAgentManager.getAgentWithModule("nfc-mfultralight")!, "nfc-mfultralight-write", btoa(output)).then(async (response) => {
            let data;

            try {
                data = await response.json();
            }
            catch(ex) {
                setNfcStatus("program-error");
                return;
            }

            if (typeof data === "object" && data.error) {
                setNfcStatus("program-error");
                return;
            }

            setNfcRefresh(nfcRefresh + 1);
        }).catch(() => {
            setNfcStatus("program-error");
        });
    }, [serial, nfcRefresh]);

    const flashStella = useCallback(() => {
        setStellaDataStatus("programming");
        setFlashStart(true);
    }, [true]);

    // Getting stella bio IP from a serial number, https://direct-hub.egzotech.com/api/check/WQW5YJJV,
    // the stella bio must be powered on and connected to wifi

    useEffect(() => {
        if (readMacRefresh === 0) {
            return;
        }

        setMacState("read");
        setStellaData(null);

        utilsWsReq(egzotechAgentManager.getAgentWithModule("stella-flash")!, "stella-mac", "", 1000).then(async (response) => {
            if (!response.ok) {
                setMac("");
                setMacState("error");
                return;
            }

            const info = await response.json();

            if (info.length === 0) {
                setMac("");
                setMacState("error");
            }
            else {
                setMac(info.mac);
                setMacState("success");
                // setStellaDataStatus("read");

                setTimeout(() => {
                    document.querySelector('.stella-bio-flash-page .step-2')!.scrollIntoView({
                        block: "start",
                        behavior: 'smooth'
                    });

                    if (autoProgramming) {
                        flashStella();
                    }
                }, 500);
            }
        });
    }, [readMacRefresh, autoProgramming]);

    useEffect(() => {
        if (!mac) {
            return;
        }

        addProductToDatabase("StellaBIO", mac);
    }, [mac]);

    useEffect(() => {
        if (hasPrintedLabel) {
            setTimeout(() => {
                document.querySelector('.stella-bio-flash-page .finish')!.scrollIntoView({
                    block: "start",
                    behavior: 'smooth'
                });
            }, 500);
        }
    }, [hasPrintedLabel]);

    // useTimeout((repeat) => {
    //     if (!mac) {
    //         return;
    //     }

    //     setStellaData(null);

    //     try {
    //         fetch(`https://direct-hub.egzotech.com/api/check/${serial}`).then(async (response) => {
    //             const data = await response.json();

    //             if (data.info) {
    //                 console.log(data);
    //                 setStellaData(data.info);
    //                 setStellaDataStatus("done");

    //                 setTimeout(() => {
    //                     document.querySelector('.stella-bio-flash-page .step-3')!.scrollIntoView({
    //                         block: "start",
    //                         behavior: 'smooth'
    //                     });
    //                 }, 500);
    //             }
    //             else {
    //                 setStellaDataStatus("program");
    //                 repeat();
    //             }
    //         });
    //     }
    //     catch(ex) {
    //         repeat();
    //     }
    // }, 2000, [mac]);

    useEffect(() => {
        if (!mac || !stellaData) {
            return;
        }

        setNfcReady("");

        utilsWsReq(egzotechAgentManager.getAgentWithModule("nfc-mfultralight")!, "nfc-mfultralight-read").then(async (response) => {
            const data = await response.json();

            if (!Array.isArray(data)) {
                setNfcStatus("no-program");
                return;
            }

            try {
                const ndef = decodeMessage(data);

                if (!ndef || !ndef[0] || !ndef[0].value || typeof ndef[0].value !== "string") {
                    setNfcStatus("no-program");
                    return;
                }

                const value = ndef[0].value as string;

                if (value != `https://app.egzotech.com/nfc/${serial}`) {
                    setNfcStatus("error");
                    return;
                }

                setNfcStatus("ok");
                setNfcReady(ndef[0].value);

                setTimeout(() => {
                    document.querySelector('.stella-bio-flash-page .step-4')!.scrollIntoView({
                        block: "start",
                        behavior: 'smooth'
                    });
                }, 500);
            }
            catch(ex) {
                setNfcStatus("no-program");
                return;
            }
        }).catch(ex => {
            if (autoProgramming) {
                setTimeout(() => {
                    setNfcRefresh(nfcRefresh + 1);
                }, 5000);
                return;
            }

            setNfcStatus("error");
        });
    }, [mac, stellaData, nfcRefresh]);

    useEffect(() => {
        if (autoProgramming) {
            if (nfcStatus === "no-program" || nfcStatus === "error") {
                programNFC();
            }
        }
    }, [nfcStatus]);

    useEventSource<{ hasErrors: boolean, output: string }>(utilsEventUrl(egzotechAgentManager.getAgentWithModule("stella-flash")!, "stella-flash"), flashStart, {
        onOpen: (userdata) => {
            userdata.hasErrors = false;
            userdata.output = "";
            setFlashConsoleOutput(userdata.output);

            setStellaDataStatus("programming");
            setStellaData(null);
            setFlashProgress(0);
        },
        onMessage: (data, userdata) => {
            let output = userdata.output + data.replace(/(\\n)/gm, "\n").replace(/(\\r)/gm, "\r");
            output = output.replace(/\r[^\n\r]+\r/, '\r');
            userdata.output = output;
            setFlashConsoleOutput(userdata.output);

            const progressRegex = /(\d+)\s*%/m;
            const progressMatch = progressRegex.exec(data);

            if (progressMatch) {
                setFlashProgress(parseInt(progressMatch[1]));
            }

            setTimeout(() => {
                if (flashConsoleOutputRef.current) {
                    flashConsoleOutputRef.current.scrollTo(0, flashConsoleOutputRef.current.scrollHeight);
                }
            }, 0);
        },
        onClose: (reconnect, userdata) => {
            if (userdata.hasErrors) {
                return;
            }

            setFlashStart(false);
            setStellaDataStatus("done");
            setStellaData({});

            setTimeout(() => {
                document.querySelector('.stella-bio-flash-page .step-3')!.scrollIntoView({
                    block: "start",
                    behavior: 'smooth'
                });
            }, 500);
        },
        onError: (error, recconect, userdata) => {
            setFlashStart(false);
            setStellaDataStatus("error");
            userdata.hasErrors = true;
        },
        onFatal: (userdata) => {
            setFlashStart(false);
            setStellaDataStatus("error");
            userdata.hasErrors = true;
        },
        customEvents: {
            dataerror: (data, userdata) => {
                setFlashStart(false);
                setStellaDataStatus("error");
                userdata.hasErrors = true;
            }
        }
    }, [autoProgramming]);

    return <div class="stella-bio-flash-page">
        <div class="stepper">
            <div class="steps">
                <div class={"step" + (mac ? " done success" : " current")} data-index="1">{autoProgramming ? "Odczytaj dane oraz zaprogramuj Stellę" : "Odczytaj adres MAC i numer seryjny"}</div>
            </div>
        </div>
        <div class="mac paper">
            <div>
                <h1>Informacje o Stelli</h1>
                {!mac ?
                    !macState ? <Alert type="info">Podłącz Stelle do programatora i odczytaj adres MAC aby kontynuować</Alert>
                        : macState === "read" ? <span style="display: flex; align-items: center"><Alert type="info">Odczytywanie...</Alert><span style="margin-left: 10px" class="loader"/></span>
                        : macState === "error" ? <Alert type="error">Nie udało się odczytać danych</Alert> : null
                    : <Alert type="success">Odczytano MAC poprawnie</Alert>}
            </div>
            <div class="mac paper-2">
                <h2>Adres MAC</h2>
                <div class="value">{mac}</div>
            </div>

            <div class="serial paper-2">
                <h2>Numer seryjny</h2>
                <div class="value">{serial ? serial : null}</div>
            </div>

            <div>
                <label><input type="checkbox" checked={autoProgramming} onChange={(e) => setAutoProgramming(e.currentTarget.checked)}/>Automatyczne programowanie</label>
            </div>

            <div style="margin-top: 10px">
                <button type="button" onClick={() => {
                    setMac("");
                    setReadMacRefresh(readMacRefresh + 1);
                }}>{autoProgramming ? "Rozpocznij odczyt i programowanie" : "Odczytaj adres MAC i numer seryjny"}</button>
            </div>
        </div>
        {mac ? <div>
            <div class="stepper step-2">
                <div class="steps">
                    <div class={"step" + (mac && stellaData ? " done success" : " current")} data-index="2" onDblClick={() => {
                        if (!stellaData && !flashStart) {
                            setStellaData({});
                            setStellaDataStatus("done");
                        }
                    }}>Zaprogramuj Stellę</div>
                </div>
            </div>
            <div class="paper flash">
                <h1>Programowanie Stelli</h1>
                <div>
                    {stellaDataStatus === "read" ? <Alert type="info">Odczytywanie danych ze Stelli...</Alert> :
                        stellaDataStatus === "error" ? <Alert type="error">Błąd podczas programowania Stelli</Alert> :
                        stellaDataStatus === "done" ? <Alert type="success">Stella została zaprogramowana</Alert> :
                        stellaDataStatus === "program" ? <Alert type="info">Zaprogramuj Stellę</Alert> :
                        stellaDataStatus === "programming" ? <span style="display: flex; align-items: center"><Alert type="info">Programowanie Stelli...</Alert><span style="margin-left: 10px" class="loader"/></span> :
                        null}
                </div>
                {!autoProgramming ? <div class="stella-program">
                    <button type="button" class={flashStart ? "disabled" : ""} onClick={() => {
                        flashStella();
                    }}>Zaprogramuj</button>
                </div> : null}
                <div class="console-output-container">
                    <div>Informacje z programatora</div>
                    <div ref={flashConsoleOutputRef} class="console-output">{flashConsoleOutput}</div>
                </div>
                <div class="flash-progress">
                    <progress min={0} max={100} value={flashProgress}/>
                </div>
                {stellaData && Object.keys(stellaData).length ? <div class="software paper-2">
                    <h2>Dane o oprogramowaniu</h2>
                    <div class="table">
                        <div>Nazwa systemu</div>
                        <div>{stellaData && stellaData.device ? stellaData.device.os.name : null}</div>
                        <div>Wersja systemu</div>
                        <div>{stellaData && stellaData.device ? stellaData.device.os.version : null}</div>
                        <div>Adres IP</div>
                        <div>{stellaData && stellaData.net ? stellaData.net.local.address : null}</div>
                    </div>
                </div> : null}
            </div>
            {stellaData ? [
                <div class="stepper step-3">
                    <div class="steps">
                        <div class={"step" + (mac && stellaData && nfcReady ? " done success" : mac && stellaData ? " current" : "")} data-index="3">
                            Zaprogramuj tag NFC
                        </div>
                    </div>
                </div>,
                <div class="paper nfc">
                    <h1>Programowanie NFC</h1>
                    {nfcStatus === "no-program" ? <Alert type="info">NFC nie jest zaprogramowane</Alert> :
                        nfcStatus === "ok" ? <Alert type="success">NFC zostało zaprogramowane</Alert> :
                        nfcStatus === "program" ? <Alert type="info">Programowanie...</Alert> :
                        nfcStatus === "program-error" ? <Alert type="error">Nie udało się zaprogramować NFC</Alert> :
                        nfcStatus === "error" ? <Alert type="error">Dane na NFC są nieprawidłowe. Zaprogramuj jeszcze raz</Alert> :
                        nfcStatus === "" ? <Alert type="info">Odczytywanie danych...</Alert> :
                        null}
                    {!autoProgramming ? <div class="program">
                        <button class={nfcStatus === "program" || nfcStatus === "" || nfcStatus === "ok" ? "disabled" : ""} onClick={() => {
                            programNFC();
                        }}>Zaprogramuj</button>
                    </div> : null}
                    {nfcReady ? <div class="result paper-2">
                        <h2>Zapisane dane na NFC</h2>
                        <div>{nfcReady}</div>
                    </div> : null}
                </div>
            ] : null}
            {mac && stellaData && nfcReady ? [<div class="stepper step-4">
                <div class="steps">
                    <div class={"step" + (mac && stellaData && nfcReady && hasPrintedLabel ? " done success" : mac && stellaData ? " current" : "")} data-index="4" onClick={() => {
                        setHasPrintedLabel(!hasPrintedLabel);
                    }}>Wydrukuj naklejkę z numerem seryjnym</div>
                </div>
            </div>,
            <div class="print paper">
                <h1>Drukowanie</h1>
                <TemplatePrint template={stellaBIOTemplate} fields={generateLabelFields({ serialNumber: serial })} copies={1} onPrint={() => {
                    setHasPrintedLabel(true);
                }} tapeImgBase={resolvePath("images/tapes/")}/>
                <input type="checkbox" class="popup-visible" id="popup-visible-controller-label"/>
                <div class="popup">
                    <label class="background" for="popup-visible-controller-label"></label>
                    <div class="content" style="display: flex; flex-direction: column; padding: 10px; width: 800px; height: 800px;">
                        <label for="popup-visible-controller-label" class="button icon flat" style="align-self: flex-end;">close</label>
                        <div style="flex: 1 1 0; margin-top: 10px; align-self: center;">
                            <ImageSlider width={700} height={700} images={[
                                resolvePath("images/labeling/stella-3.jpg"),
                                resolvePath("images/labeling/stella-2.jpg"),
                                resolvePath("images/labeling/stella-1.jpg")
                            ]}/>
                        </div>
                    </div>
                </div>
                <label class="button secondary" for="popup-visible-controller-label">Informacje o naklejaniu</label>
            </div>] : null}
            {mac && stellaData && hasPrintedLabel ? <div class="finish">
                <button type="button" onClick={() => {
                    route("/stella-bio/serial-number", true);
                }}>Wygeneruj naklejki dla akcesoriów</button>
            </div> : null}
        </div> : null }
    </div>;
}
